import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { GoldButton, Logo } from "../components/shared/UIComponents";
import axios from "axios";
import {
  Building2, User, Shield, Upload, ChevronRight, ChevronLeft,
  CheckCircle, Briefcase, AlertCircle, FileText, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 0, label: "Entity Info", icon: Building2, description: "Tell us about your investment entity" },
  { id: 1, label: "Accreditation", icon: Shield, description: "Verify your accredited investor status" },
  { id: 2, label: "Documents", icon: Upload, description: "Upload verification documents" },
  { id: 3, label: "Preferences", icon: Briefcase, description: "Set your investment preferences" },
];

export default function InvestorOnboardingPage({ onComplete }) {
  const { user, token } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({});
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    entity_type: "individual",
    entity_name: "",
    tax_id: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
    accreditation_method: "income_verification",
    accreditation_details: "",
    risk_tolerance: "moderate",
    target_annual_return: 12,
    preferred_hold_period: "12-24 months",
    investment_experience_years: 0,
    notes: "",
  });
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/investor/profile/options`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setOptions(res.data))
      .catch(() => {});
  }, [token]);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setError(""); };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/investor/profile/onboarding`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.status === "success") {
        // Attach any uploaded documents to the profile
        for (const doc of uploadedDocs) {
          try {
            await axios.post(`${API}/investor/profile/documents?doc_url=${encodeURIComponent(doc.doc_url)}&doc_name=${encodeURIComponent(doc.doc_name)}`, null, {
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch {}
        }
        setStep(4); // Success step
        setTimeout(() => {
          onComplete({
            ...user,
            onboarding_complete: true,
            investor_onboarding_complete: true,
            accreditation_status: "pending_review",
          });
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit profile");
    } finally {
      setLoading(false);
    }
  };

  const entityLabels = {
    individual: "Individual", llc: "LLC", trust: "Trust", corporation: "Corporation",
    partnership: "Partnership", ira_self_directed: "Self-Directed IRA", family_office: "Family Office",
  };

  const accredLabels = options.accreditation_labels || {
    income_verification: "Income ($200K+ individual / $300K+ joint)",
    net_worth_verification: "Net Worth ($1M+ excluding primary residence)",
    professional_certification: "Professional Certification (Series 7, 65, 82)",
    third_party_letter: "Third-Party Verification Letter",
    entity_qualification: "Entity with $5M+ in Investments",
  };

  const inputClass = "w-full rounded-lg border border-emerald-800/30 bg-emerald-950/50 px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold/60 focus:ring-1 focus:ring-gold/30 outline-none transition-colors";
  const labelClass = "block text-sm font-medium text-cream/70 mb-1.5";
  const selectClass = `${inputClass} appearance-none cursor-pointer`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-[#022c22] to-emerald-950 flex flex-col" data-testid="investor-onboarding-page">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-emerald-700/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-gold/5 rounded-full blur-[120px]" />
      </div>

      <div className="relative p-6 flex items-center justify-center">
        <Logo size="default" showTagline={false} />
      </div>

      <div className="relative flex-1 flex items-start justify-center px-4 pb-12">
        <div className="w-full max-w-2xl">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-10" data-testid="onboarding-stepper">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = step > i;
              const active = step === i;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center gap-2 relative">
                    <motion.div
                      className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                        done ? "bg-gold border-gold" : active ? "border-gold/80 bg-gold/20" : "border-emerald-700/40 bg-emerald-900/30"
                      }`}
                      animate={active ? { scale: [1, 1.08, 1] } : {}}
                      transition={{ repeat: Infinity, duration: 2 }}
                    >
                      {done ? <CheckCircle className="h-5 w-5 text-emerald-950" /> : <Icon className={`h-5 w-5 ${active ? "text-gold" : "text-cream/40"}`} />}
                    </motion.div>
                    <span className={`text-xs font-medium ${active ? "text-gold" : done ? "text-cream/70" : "text-cream/40"}`}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 mt-[-22px] rounded-full transition-all duration-500 ${done ? "bg-gold/60" : "bg-emerald-800/30"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            {step === 0 && (
              <StepCard key="step0" title="Entity Information" desc="Tell us about how you'll be investing">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className={labelClass}>Entity Type</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {Object.entries(entityLabels).map(([k, v]) => (
                        <button key={k} onClick={() => set("entity_type", k)} data-testid={`entity-type-${k}`}
                          className={`px-3 py-2.5 rounded-lg border text-sm transition-all ${
                            form.entity_type === k ? "border-gold bg-gold/15 text-gold font-medium" : "border-emerald-800/30 text-cream/60 hover:border-cream/30"
                          }`}>{v}</button>
                      ))}
                    </div>
                  </div>
                  {form.entity_type !== "individual" && (
                    <div className="col-span-2">
                      <label className={labelClass}>Entity Name</label>
                      <input value={form.entity_name} onChange={e => set("entity_name", e.target.value)} placeholder="Entity legal name" className={inputClass} data-testid="entity-name-input" />
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>Tax ID / SSN (Last 4)</label>
                    <input value={form.tax_id} onChange={e => set("tax_id", e.target.value)} placeholder="••••" maxLength={4} className={inputClass} data-testid="tax-id-input" />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="(555) 123-4567" className={inputClass} data-testid="phone-input" />
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Address</label>
                    <input value={form.address} onChange={e => set("address", e.target.value)} placeholder="Street address" className={inputClass} data-testid="address-input" />
                  </div>
                  <div>
                    <label className={labelClass}>City</label>
                    <input value={form.city} onChange={e => set("city", e.target.value)} placeholder="City" className={inputClass} data-testid="city-input" />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    <input value={form.state} onChange={e => set("state", e.target.value)} placeholder="TX" maxLength={2} className={inputClass} data-testid="state-input" />
                  </div>
                  <div>
                    <label className={labelClass}>ZIP Code</label>
                    <input value={form.zip_code} onChange={e => set("zip_code", e.target.value)} placeholder="75001" className={inputClass} data-testid="zip-input" />
                  </div>
                </div>
              </StepCard>
            )}

            {step === 1 && (
              <StepCard key="step1" title="Accreditation Verification" desc="Select how you qualify as an accredited investor">
                <div className="space-y-3">
                  {Object.entries(accredLabels).map(([k, v]) => (
                    <button key={k} onClick={() => set("accreditation_method", k)} data-testid={`accred-method-${k}`}
                      className={`w-full text-left px-4 py-4 rounded-lg border transition-all ${
                        form.accreditation_method === k ? "border-gold bg-gold/10 text-gold" : "border-emerald-800/30 text-cream/70 hover:border-cream/30"
                      }`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          form.accreditation_method === k ? "border-gold" : "border-cream/30"
                        }`}>
                          {form.accreditation_method === k && <div className="w-2.5 h-2.5 rounded-full bg-gold" />}
                        </div>
                        <span className="text-sm">{v}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-4">
                  <label className={labelClass}>Additional Details (Optional)</label>
                  <textarea value={form.accreditation_details} onChange={e => set("accreditation_details", e.target.value)}
                    placeholder="Any additional context about your accreditation status..." rows={3} className={inputClass} data-testid="accred-details-input" />
                </div>
              </StepCard>
            )}

            {step === 2 && (
              <StepCard key="step2" title="Verification Documents" desc="Upload supporting documentation for your accreditation">
                <div className="text-center py-8 border-2 border-dashed border-emerald-800/30 rounded-xl">
                  {uploadedDocs.length === 0 && !uploading && (
                    <>
                      <FileText className="h-12 w-12 text-cream/30 mx-auto mb-3" />
                      <p className="text-sm text-cream/60 mb-2">Upload W-9, accreditation letter, or financial statements</p>
                      <p className="text-xs text-cream/40 mb-4">PDF, JPG, PNG up to 20MB</p>
                    </>
                  )}
                  {uploading && (
                    <div className="flex items-center justify-center gap-2 py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-gold" />
                      <span className="text-sm text-cream/60">Uploading...</span>
                    </div>
                  )}
                  {uploadedDocs.length > 0 && (
                    <div className="space-y-2 mb-4 text-left px-4">
                      {uploadedDocs.map((doc, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-800/20 border border-emerald-700/30">
                          <CheckCircle className="h-4 w-4 text-gold shrink-0" />
                          <span className="text-sm text-cream/80 truncate flex-1">{doc.doc_name}</span>
                          <span className="text-xs text-cream/40">{(doc.size / 1024).toFixed(0)} KB</span>
                          <button onClick={() => setUploadedDocs(d => d.filter((_, idx) => idx !== i))} className="text-cream/30 hover:text-red-400 transition-colors" data-testid={`remove-doc-${i}`}>
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold/15 border border-gold/30 rounded-lg text-sm text-gold cursor-pointer hover:bg-gold/25 transition-colors" data-testid="upload-document-btn">
                    <Upload className="h-4 w-4" />
                    {uploadedDocs.length > 0 ? "Add Another File" : "Choose File"}
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploading(true);
                        setError("");
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const res = await axios.post(`${API}/investor/profile/upload`, fd, {
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
                          });
                          setUploadedDocs(prev => [...prev, res.data]);
                        } catch (err) {
                          setError(err.response?.data?.detail || "Upload failed");
                        } finally {
                          setUploading(false);
                          e.target.value = "";
                        }
                      }} />
                  </label>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-emerald-800/20 border border-emerald-700/30">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                    <p className="text-xs text-cream/60">
                      Document upload is optional at this stage. Our compliance team may request additional documentation during the review process.
                      You can also upload documents later from your investor profile.
                    </p>
                  </div>
                </div>
              </StepCard>
            )}

            {step === 3 && (
              <StepCard key="step3" title="Investment Preferences" desc="Help us match you with the right opportunities">
                <div className="space-y-5">
                  <div>
                    <label className={labelClass}>Risk Tolerance</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {["conservative", "moderate", "aggressive"].map(r => (
                        <button key={r} onClick={() => set("risk_tolerance", r)} data-testid={`risk-${r}`}
                          className={`px-4 py-3 rounded-lg border text-sm capitalize transition-all ${
                            form.risk_tolerance === r ? "border-gold bg-gold/15 text-gold font-medium" : "border-emerald-800/30 text-cream/60 hover:border-cream/30"
                          }`}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Target Annual Return (%)</label>
                    <input type="number" value={form.target_annual_return} onChange={e => set("target_annual_return", parseFloat(e.target.value) || 0)}
                      min={0} max={50} className={inputClass} data-testid="target-return-input" />
                  </div>
                  <div>
                    <label className={labelClass}>Preferred Hold Period</label>
                    <select value={form.preferred_hold_period} onChange={e => set("preferred_hold_period", e.target.value)}
                      className={selectClass} data-testid="hold-period-select">
                      <option value="0-12 months">0-12 months</option>
                      <option value="12-24 months">12-24 months</option>
                      <option value="24-60 months">24-60 months</option>
                      <option value="60+ months">60+ months</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelClass}>Investment Experience (Years)</label>
                    <input type="number" value={form.investment_experience_years} onChange={e => set("investment_experience_years", parseInt(e.target.value) || 0)}
                      min={0} max={50} className={inputClass} data-testid="experience-years-input" />
                  </div>
                  <div>
                    <label className={labelClass}>Notes (Optional)</label>
                    <textarea value={form.notes} onChange={e => set("notes", e.target.value)}
                      placeholder="Anything else you'd like us to know..." rows={2} className={inputClass} data-testid="notes-input" />
                  </div>
                </div>
              </StepCard>
            )}

            {step === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16" data-testid="onboarding-success">
                <motion.div
                  className="w-20 h-20 rounded-full bg-gold/20 border-2 border-gold mx-auto flex items-center justify-center mb-6"
                  initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.2 }}
                >
                  <CheckCircle className="h-10 w-10 text-gold" />
                </motion.div>
                <h2 className="text-2xl font-bold text-cream mb-3">Profile Submitted</h2>
                <p className="text-cream/60 max-w-md mx-auto mb-2">Your accreditation is under review. Our team will verify your status within 24-48 hours.</p>
                <p className="text-sm text-gold/70">Redirecting to your dashboard...</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2" data-testid="onboarding-error">
              <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Navigation */}
          {step < 4 && (
            <div className="flex items-center justify-between mt-8">
              <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} data-testid="onboarding-back-btn"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  step === 0 ? "text-cream/20 cursor-not-allowed" : "text-cream/60 hover:text-cream border border-emerald-800/30 hover:border-cream/30"
                }`}>
                <ChevronLeft className="h-4 w-4" /> Back
              </button>

              {step < 3 ? (
                <GoldButton onClick={() => setStep(s => s + 1)} data-testid="onboarding-next-btn">
                  Continue <ChevronRight className="h-4 w-4 inline ml-1" />
                </GoldButton>
              ) : (
                <GoldButton onClick={handleSubmit} disabled={loading} data-testid="onboarding-submit-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> : null}
                  Submit for Review
                </GoldButton>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepCard({ title, desc, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-emerald-900/30 border border-emerald-800/30 rounded-2xl p-6 sm:p-8"
    >
      <h2 className="text-xl font-bold text-cream mb-1">{title}</h2>
      <p className="text-sm text-cream/50 mb-6">{desc}</p>
      {children}
    </motion.div>
  );
}
